import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    LogOut,
    Phone,
    Mail,
    MapPin,
    User,
    Calendar,
    CheckCircle2,
    Trash2,
    Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "closed"];

const STATUS_STYLES = {
    new: "bg-copper text-bone",
    contacted: "bg-ember text-ink",
    scheduled: "bg-moss text-bone",
    closed: "bg-line text-ink",
};

export default function Admin() {
    const [pin, setPin] = useState("");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [filter, setFilter] = useState("all");

    const fetchQuotes = useCallback(
        async (overridePin) => {
            const usePin = overridePin || pin;
            setLoading(true);
            try {
                const res = await axios.get(`${API}/quotes`, {
                    headers: { "X-Admin-Pin": usePin },
                });
                setQuotes(res.data || []);
                setAuthed(true);
                if (overridePin) {
                    sessionStorage.setItem("haz-admin-pin", overridePin);
                }
            } catch (err) {
                if (err.response?.status === 401) {
                    toast.error("Invalid pin");
                    setAuthed(false);
                    sessionStorage.removeItem("haz-admin-pin");
                } else {
                    toast.error("Failed to load quotes");
                }
            } finally {
                setLoading(false);
            }
        },
        [pin]
    );

    useEffect(() => {
        const saved = sessionStorage.getItem("haz-admin-pin");
        if (saved) {
            setPin(saved);
            fetchQuotes(saved);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitPin = async (e) => {
        e.preventDefault();
        if (!pin) return;
        await fetchQuotes(pin);
    };

    const logout = () => {
        sessionStorage.removeItem("haz-admin-pin");
        setAuthed(false);
        setPin("");
        setQuotes([]);
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(
                `${API}/quotes/${id}`,
                { status },
                { headers: { "X-Admin-Pin": pin } }
            );
            setQuotes((q) => q.map((it) => (it.id === id ? { ...it, status } : it)));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Failed to update");
        }
    };

    const removeQuote = async (id) => {
        if (!window.confirm("Delete this quote? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/quotes/${id}`, {
                headers: { "X-Admin-Pin": pin },
            });
            setQuotes((q) => q.filter((it) => it.id !== id));
            toast.success("Deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    if (!authed) {
        return (
            <main
                data-testid="admin-login"
                className="min-h-screen bg-ink text-bone flex items-center justify-center px-6"
            >
                <form
                    onSubmit={submitPin}
                    className="bg-bone text-ink p-10 md:p-14 w-full max-w-md"
                >
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-3">
                        HAZ Admin
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-8">Enter access pin</h1>
                    <label
                        htmlFor="pin"
                        className="text-xs font-bold tracking-wider uppercase text-ink mb-2 block"
                    >
                        Admin Pin
                    </label>
                    <input
                        id="pin"
                        data-testid="admin-pin-input"
                        type="password"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        autoFocus
                        className="w-full border-b-2 border-line bg-transparent py-3 focus:outline-none focus:border-copper transition-colors"
                    />
                    <button
                        type="submit"
                        data-testid="admin-login-btn"
                        disabled={loading}
                        className="btn-lift mt-8 w-full bg-copper text-bone font-semibold py-4 hover:bg-copper-hover disabled:opacity-60"
                    >
                        {loading ? "Verifying…" : "Sign In"}
                    </button>
                </form>
            </main>
        );
    }

    const filtered =
        filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-bone pt-10 pb-20">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-3">
                            HAZ Admin
                        </div>
                        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                            Quote Requests
                        </h1>
                        <p className="text-muted_ink mt-2">
                            {quotes.length} total · {quotes.filter((q) => q.status === "new").length} new
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => fetchQuotes()}
                            data-testid="admin-refresh-btn"
                            className="btn-lift border-2 border-ink text-ink font-semibold py-3 px-5 hover:bg-ink hover:text-bone"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-btn"
                            className="btn-lift inline-flex items-center gap-2 bg-ink text-bone font-semibold py-3 px-5 hover:bg-copper"
                        >
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>

                {/* Filter */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {["all", ...STATUS_OPTIONS].map((s) => {
                        const count =
                            s === "all" ? quotes.length : quotes.filter((q) => q.status === s).length;
                        return (
                            <button
                                key={s}
                                onClick={() => setFilter(s)}
                                data-testid={`admin-filter-${s}`}
                                className={`text-xs uppercase tracking-wider font-bold px-4 py-2 border transition-colors ${
                                    filter === s
                                        ? "bg-ink text-bone border-ink"
                                        : "border-line text-ink hover:border-ink"
                                }`}
                            >
                                {s} · {count}
                            </button>
                        );
                    })}
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="animate-spin text-copper" size={32} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="admin-empty-state"
                        className="bg-white border border-line p-14 text-center"
                    >
                        <CheckCircle2
                            size={36}
                            strokeWidth={1.5}
                            className="text-moss mx-auto mb-4"
                        />
                        <h3 className="font-heading text-2xl font-bold">No quote requests yet</h3>
                        <p className="text-muted_ink mt-2">
                            New leads from the website will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {filtered.map((q) => (
                            <article
                                key={q.id}
                                data-testid={`admin-quote-${q.id}`}
                                className="bg-white border border-line p-7 flex flex-col gap-4"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <User
                                                size={16}
                                                strokeWidth={1.5}
                                                className="text-muted_ink"
                                            />
                                            <h3 className="font-heading font-bold text-xl">
                                                {q.name}
                                            </h3>
                                        </div>
                                        <div className="text-xs uppercase tracking-wider text-muted_ink mt-1">
                                            {q.audience}
                                            {q.city && ` · ${q.city}`}
                                        </div>
                                    </div>
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 ${STATUS_STYLES[q.status]}`}
                                    >
                                        {q.status}
                                    </span>
                                </div>

                                <div className="space-y-1.5 text-sm">
                                    <a
                                        href={`tel:${q.phone}`}
                                        className="flex items-center gap-2 hover:text-copper"
                                    >
                                        <Phone size={14} strokeWidth={1.5} /> {q.phone}
                                    </a>
                                    <a
                                        href={`mailto:${q.email}`}
                                        className="flex items-center gap-2 hover:text-copper"
                                    >
                                        <Mail size={14} strokeWidth={1.5} /> {q.email}
                                    </a>
                                    {q.address && (
                                        <div className="flex items-center gap-2 text-muted_ink">
                                            <MapPin size={14} strokeWidth={1.5} /> {q.address}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 text-muted_ink">
                                        <Calendar size={14} strokeWidth={1.5} />
                                        {new Date(q.created_at).toLocaleString()}
                                    </div>
                                </div>

                                {q.services?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {q.services.map((s) => (
                                            <span
                                                key={s}
                                                className="text-[10px] uppercase tracking-wider font-bold border border-line px-2.5 py-1"
                                            >
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {q.message && (
                                    <p className="text-sm text-muted_ink leading-relaxed border-l-2 border-copper pl-4">
                                        {q.message}
                                    </p>
                                )}

                                <div className="flex flex-wrap gap-2 pt-3 border-t border-line">
                                    {STATUS_OPTIONS.filter((s) => s !== q.status).map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => updateStatus(q.id, s)}
                                            data-testid={`admin-set-${s}-${q.id}`}
                                            className="text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 border border-line hover:border-ink hover:bg-ink hover:text-bone transition-colors"
                                        >
                                            Mark {s}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => removeQuote(q.id)}
                                        data-testid={`admin-delete-${q.id}`}
                                        className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 text-destructive hover:bg-destructive hover:text-bone border border-destructive transition-colors"
                                    >
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
